import { useNavigate } from "react-router-dom";
import { Lightbulb, Building2, ArrowRight, Check } from "lucide-react";
import { Seo, SITE_URL } from "@/components/Seo";

const PANELS = [
  {
    key: "gruender",
    to: "/gruender",
    eyebrow: "Für Gründer",
    icon: Lightbulb,
    title: "Ich habe eine Geschäftsidee",
    sub: "Finde heraus, wie viel Potenzial in deiner Idee steckt – und worauf du vor dem Start genauer achten solltest.",
    bullets: ["0–100 Potenzial-Score für deine Idee", "Markt, Wettbewerb, Risiken & Förderung", "Konkreter 30-Tage-Plan", "Kompletter PDF-Report für 29 €"],
    cta: "Geschäftsidee prüfen",
    gradient: "linear-gradient(150deg, #0b7d5c 0%, #10C98D 55%, #3DEEA6 100%)",
    glow: "#10C98D",
  },
  {
    key: "business",
    to: "/business",
    eyebrow: "Für bestehende Unternehmen",
    icon: Building2,
    title: "Ich habe bereits ein Unternehmen",
    sub: "Finde ungenutzte Potenziale, konkrete Wachstumshebel und Schwachstellen in deinem Unternehmen.",
    bullets: ["Analyse deiner aktuellen Situation", "Potenziale in Vertrieb, Marketing & Positionierung", "Wirtschaftlichkeit, Risiken & Finanzierung", "Konkreter Maßnahmenplan als PDF-Report für 39 €"],
    cta: "Unternehmen analysieren",
    gradient: "linear-gradient(150deg, #06788c 0%, #0AA0BE 55%, #22D3DE 100%)",
    glow: "#0AA0BE",
  },
];

export default function Home() {
  const nav = useNavigate();
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Gründio",
      url: SITE_URL,
      inLanguage: "de-DE",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Gründio",
      url: SITE_URL,
      logo: `${SITE_URL}/gruendio-logo.png`,
      areaServed: ["DE"],
      description: "KI-gestützte Orientierungshilfe für Gründer und bestehende Unternehmen in Deutschland.",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#0B1512" }} data-testid="home-page">
      <Seo
        title="Gründio – KI-Report für Gründer & bestehende Unternehmen in Deutschland"
        description="Gründio bewertet deine Geschäftsidee oder dein bestehendes Unternehmen in 2 Minuten – mit Score, Wachstumshebeln, Finanzierungs-Orientierung und 30-Tage-Plan. Wähle deinen Weg: Gründer oder Business."
        path="/"
        keywords="Geschäftsidee bewerten, Unternehmen optimieren, KI-Report Gründung, Business-Check, Gründio"
        jsonLd={jsonLd}
      />

      {/* Header */}
      <header className="relative z-10 px-6 pt-7">
        <div className="max-w-6xl mx-auto flex items-center justify-center">
          <div className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="home-logo">
            <span style={{ color: "#3DEEA6" }}>Gründ</span>
            <span className="text-white">io</span>
          </div>
        </div>
        <p className="mt-3 text-center text-sm text-white/50 max-w-md mx-auto">
          Wo stehst du gerade? Wähle deinen Weg und erhalte den Report passend zu deiner Situation.
        </p>
      </header>

      {/* Split panels */}
      <main className="relative flex-1 grid md:grid-cols-2 gap-4 md:gap-0 px-4 md:px-0 py-6 md:py-0">
        {PANELS.map((p, i) => {
          const Icon = p.icon;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => nav(p.to)}
              data-testid={`home-choose-${p.key}`}
              className="group relative overflow-hidden text-left rounded-3xl md:rounded-none min-h-[42vh] md:min-h-[calc(100vh-9rem)] p-8 md:p-14 flex flex-col justify-center transition-[flex] duration-500 focus:outline-none animate-fade-in-up"
              style={{ background: p.gradient, animationDelay: `${i * 90}ms` }}
            >
              {/* dot texture + hover glow */}
              <div className="absolute inset-0 bg-dots opacity-10 pointer-events-none" />
              <div
                className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full blur-3xl opacity-40 transition-opacity duration-500 group-hover:opacity-70 pointer-events-none"
                style={{ background: p.glow }}
              />

              <div className="relative max-w-md md:mx-auto w-full text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-[11px] font-semibold uppercase tracking-widest mb-6">
                  <Icon className="h-3.5 w-3.5" /> {p.eyebrow}
                </div>

                <div className="mb-5 h-16 w-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                  <Icon className="h-8 w-8" strokeWidth={2.1} />
                </div>

                <h2 className="text-3xl md:text-4xl font-extrabold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {p.title}
                </h2>
                <p className="mt-3 text-[15px] md:text-base text-white/85 leading-relaxed">{p.sub}</p>

                <ul className="mt-6 space-y-2.5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-white/90">
                      <span className="mt-0.5 h-5 w-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                        <Check className="h-3 w-3" />
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <span className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white font-semibold text-[15px] shadow-lg transition-all group-hover:gap-3" style={{ color: p.glow }}>
                  {p.cta}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </button>
          );
        })}
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-5">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Gründio · KI-gestützte Orientierungshilfe · Keine Rechts- oder Steuerberatung</span>
          <nav className="flex items-center gap-4">
            <a href="/impressum" className="hover:text-white/80">Impressum</a>
            <a href="/datenschutz" className="hover:text-white/80">Datenschutz</a>
            <a href="/agb" className="hover:text-white/80">AGB</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
